import { kebabCase } from "lodash";

import composeFieldName from "./composeFieldName";
import formatFacetValueLabel from "../../utils/formatFacetValueLabel";
import Facet, { FacetValue } from "../../types/funnelback/Facets";
import {
    getFacetParamName,
    getFacetParamValue,
    getFacetValues,
} from "../../utils/facetParams";
import {
    SearchFieldCheckbox,
    SearchFieldRange,
    SearchFieldFacetType,
    SearchFieldOption,
    SearchFieldSelect,
    SearchFieldsType,
    SearchFieldMultiSelect,
} from "./SearchForm";

/*
 * Compose field value with multiple options
 */
const composeFieldOption = (
    facet: Facet,
    rawCategoryValue: FacetValue,
    facetId: string
): SearchFieldOption => {
    const { data, label } = rawCategoryValue;
    const id = `${facetId}-${kebabCase(data)}`;
    const option = {
        id,
        label: formatFacetValueLabel(label),
        value: getFacetParamValue(facet, rawCategoryValue),
    };
    return option;
};

type FacetFieldProps = {
    [key: string]:
        | SearchFieldCheckbox
        | SearchFieldSelect
        | SearchFieldRange
        | SearchFieldMultiSelect;
};

/**
 * List of specific facets which should be displayed as a multi select dropdown
 * @see https://jira.squiz.net/browse/PLAN-224
 */
const multiSelectFacetNames = [
    "council",
    "location",
    "type",
    "filter by council",
    "filter by centre",
    "filter by council area",
    "filter by resource type",
    "filter by program stage",
];

/*
 * Deserialise Funnelback facet category values to Formik values
 */
const composeFieldValues = (facets: Facet[]): SearchFieldsType | null => {
    const idPrefix = "planning-search-field-";
    if (!facets || !facets.length) {
        return {
            query: {
                name: "query",
                type: "text",
                label: "Keywords",
                id: `${idPrefix}query`,
                placeholder: "Search by keywords",
            },
        };
    }
    const facetFields: FacetFieldProps = {};
    facets
        .filter((facet) => getFacetValues(facet).length > 0)
        .forEach((facet) => {
            const { guessedDisplayType, name } = facet;
            const queryStringParamName = getFacetParamName(facet);
            if (!queryStringParamName) {
                return;
            }
            const values = getFacetValues(facet);
            const fieldKey = composeFieldName(queryStringParamName);
            const id = `${idPrefix}${kebabCase(fieldKey)}`;

            // Assign default facet type
            let facetType: SearchFieldFacetType = "select";

            // Check if guessedDisplayType is set
            switch (guessedDisplayType) {
                case "CHECKBOX":
                    facetType = "checkboxGroup";
                    break;
                case "DATE_RANGE":
                    facetType = "dateRange";
            }

            // Check if facets should be displayed as a multi select component
            if (
                multiSelectFacetNames.includes(facet.name.toLowerCase()) &&
                facet.selectionType === "MULTIPLE"
            ) {
                facetType = "multiSelect";
            }

            const formattedField = {
                name: fieldKey,
                facetType,
                label: name,
                id,
                options: values.map((value) =>
                    composeFieldOption(facet, value, id)
                ),
            };
            facetFields[fieldKey] = formattedField;
        });
    const fieldsConfig = {
        query: {
            name: "query",
            type: "text",
            label: "Keywords",
            id: `${idPrefix}query`,
            placeholder: "Search by keywords",
        },
        ...facetFields,
    };
    return fieldsConfig;
};

export default composeFieldValues;
